import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Inbox, Sparkles } from 'lucide-react'
import { useProfile } from '@/api/profile'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'

/**
 * Annonce de la boîte de réception unifiée (26 sept 2026).
 *
 * Une fois, aux comptes créés AVANT la mise en ligne ; jamais aux nouveaux,
 * qui découvrent l'écran tel quel. Plus rien après le 26 oct : quelqu'un qui
 * revient dans six mois ne tombe pas sur une vieille nouveauté. Le « déjà vu »
 * est gardé dans le navigateur (utilisateurs seuls, un poste).
 */
const MISE_EN_LIGNE = Date.parse('2026-09-26T00:00:00-04:00')
const FIN = Date.parse('2026-10-26T00:00:00-04:00')
const CLE = 'callizy:nouveaute-boite-2026-09'

function dejaVu(): boolean {
  try {
    return localStorage.getItem(CLE) !== null
  } catch {
    return true // stockage bloqué : on ne l'impose pas à chaque visite
  }
}

export function NouveauteBoite({ onOuvrir }: { onOuvrir: () => void }) {
  const { t } = useTranslation()
  const { data: profil } = useProfile()
  const [ferme, setFerme] = useState(dejaVu)

  const inscrit = profil?.date_joined ? Date.parse(profil.date_joined) : NaN
  const visible = !ferme && Date.now() < FIN && Number.isFinite(inscrit) && inscrit < MISE_EN_LIGNE

  const fermer = () => {
    try {
      localStorage.setItem(CLE, new Date().toISOString())
    } catch {
      /* rien : la fenêtre se ferme quand même */
    }
    setFerme(true)
  }

  return (
    <Dialog open={visible} onOpenChange={(ouvert) => !ouvert && fermer()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300">
              <Inbox className="h-5 w-5" />
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
              <Sparkles className="h-3 w-3" />
              {t('nouveaute.badge', 'Nouveauté')}
            </span>
          </div>
          <DialogTitle className="text-lg">{t('nouveaute.boite.titre', 'Tout au même endroit')}</DialogTitle>
          <DialogDescription>
            {t(
              'nouveaute.boite.texte',
              "Appels, textos et messages vocaux d'une personne sont maintenant réunis dans une seule conversation.",
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            className="bg-indigo-600 text-white hover:bg-indigo-700"
            onClick={() => {
              fermer()
              onOuvrir()
            }}
          >
            {t('nouveaute.boite.bouton', 'Voir mes conversations')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
